// utils/idb.js - 완전한 IndexedDB 데이터 관리 시스템

const DB_NAME = 'fcfunnel_db';
const DB_VERSION = 3; // 버전 업그레이드 (workDiaries 추가)

let dbPromise = null;
let dbInstance = null;

export function openDb() {
    if (dbPromise) return dbPromise;

    // IndexedDB 지원 여부 확인
    if (!window.indexedDB) {
        const error = new Error('IndexedDB가 지원되지 않는 브라우저입니다.');
        console.error('❌ IndexedDB 미지원:', error);
        return Promise.reject(error);
    }

    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = req.result;
            const oldVersion = e.oldVersion;

            // 고객 스토어: key = id (number), index by stage
            if (!db.objectStoreNames.contains('customers')) {
                const store = db.createObjectStore('customers', { keyPath: 'id' });
                store.createIndex('by_stage', 'stage', { unique: false });
                store.createIndex('by_name', 'name', { unique: false });
                store.createIndex('by_phone', 'phone', { unique: false });
            }

            // 히스토리 스토어: key = auto, index by customerId, by_date
            if (!db.objectStoreNames.contains('histories')) {
                const store = db.createObjectStore('histories', { keyPath: 'key', autoIncrement: true });
                store.createIndex('by_customer', 'customerId', { unique: false });
                store.createIndex('by_date', 'at', { unique: false });
                store.createIndex('by_customer_date', ['customerId', 'at'], { unique: false });
            }

            // 앱 설정 스토어: key = settingKey, value = settingValue
            if (!db.objectStoreNames.contains('settings')) {
                db.createObjectStore('settings', { keyPath: 'key' });
            }

            // 업무일지 스토어: key = id (auto), index by_date
            if (!db.objectStoreNames.contains('workDiaries')) {
                const store = db.createObjectStore('workDiaries', { keyPath: 'id', autoIncrement: true });
                store.createIndex('by_date', 'date', { unique: false });
            }

            // 버전 1에서 2로 업그레이드 시 기존 데이터 마이그레이션
            if (oldVersion < 2) {
                console.log('IndexedDB 버전 업그레이드: v1 → v2');
            }

            // 버전 2에서 3으로 업그레이드 시 workDiaries 스토어 추가
            if (oldVersion < 3) {
                console.log('IndexedDB 버전 업그레이드: v2 → v3 (workDiaries 추가)');
            }
        };
        req.onsuccess = () => {
            dbInstance = req.result;
            resolve(req.result);
        };
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

function tx(db, storeName, mode = 'readonly') {
    return db.transaction(storeName, mode).objectStore(storeName);
}

// ===== 고객 데이터 관리 =====

export async function idbPutCustomers(customersArray) {
    const db = await openDb();
    const store = tx(db, 'customers', 'readwrite');
    await new Promise((resolve, reject) => {
        let pending = customersArray.length;
        if (pending === 0) return resolve();
        customersArray.forEach((c) => {
            const req = store.put(c);
            req.onsuccess = () => { if (--pending === 0) resolve(); };
            req.onerror = () => reject(req.error);
        });
    });
}

export async function idbPutCustomer(customer) {
    const db = await openDb();
    const store = tx(db, 'customers', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.put(customer);
        req.onsuccess = () => resolve(customer);
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetCustomer(customerId) {
    const db = await openDb();
    const store = tx(db, 'customers');
    return new Promise((resolve, reject) => {
        const req = store.get(customerId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export async function idbDeleteCustomer(customerId) {
    const db = await openDb();
    const store = tx(db, 'customers', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.delete(customerId);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetCustomersByStage(stage, offset = 0, limit = 50) {
    const db = await openDb();
    const store = tx(db, 'customers');
    const index = store.index('by_stage');
    const result = [];
    let skipped = 0;
    return new Promise((resolve, reject) => {
        const req = index.openCursor(IDBKeyRange.only(stage));
        req.onsuccess = (e) => {
            const cursor = e.target.result;
            if (!cursor) return resolve(result);
            if (skipped < offset) {
                skipped++;
                return cursor.continue();
            }
            if (result.length >= limit) return resolve(result);
            result.push(cursor.value);
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetAllCustomers() {
    const db = await openDb();
    const store = tx(db, 'customers');
    return new Promise((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export async function idbSearchCustomers(query) {
    const db = await openDb();
    const store = tx(db, 'customers');
    const result = [];
    return new Promise((resolve, reject) => {
        const req = store.openCursor();
        req.onsuccess = (e) => {
            const cursor = e.target.result;
            if (!cursor) return resolve(result);

            const customer = cursor.value;
            const searchText = `${customer.name} ${customer.phone} ${customer.company || ''} ${customer.memo || ''}`.toLowerCase();

            if (searchText.includes(query.toLowerCase())) {
                result.push(customer);
            }
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

export async function idbClearCustomers() {
    const db = await openDb();
    const store = tx(db, 'customers', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

// ===== 히스토리 데이터 관리 =====

export async function idbPutHistories(historiesArray) {
    const db = await openDb();
    const store = tx(db, 'histories', 'readwrite');
    await new Promise((resolve, reject) => {
        let pending = historiesArray.length;
        if (pending === 0) return resolve();
        historiesArray.forEach((h) => {
            const req = store.put(h);
            req.onsuccess = () => { if (--pending === 0) resolve(); };
            req.onerror = () => reject(req.error);
        });
    });
}

export async function idbPutHistory(history) {
    const db = await openDb();
    const store = tx(db, 'histories', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.put(history);
        req.onsuccess = () => resolve(history);
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetHistory(historyKey) {
    const db = await openDb();
    const store = tx(db, 'histories');
    return new Promise((resolve, reject) => {
        const req = store.get(historyKey);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetHistoriesByCustomer(customerId, offset = 0, limit = 50) {
    const db = await openDb();
    const store = tx(db, 'histories');
    const index = store.index('by_customer');
    const result = [];
    let skipped = 0;
    return new Promise((resolve, reject) => {
        const req = index.openCursor(IDBKeyRange.only(customerId));
        req.onsuccess = (e) => {
            const cursor = e.target.result;
            if (!cursor) return resolve(result);
            if (skipped < offset) {
                skipped++;
                return cursor.continue();
            }
            if (result.length >= limit) return resolve(result);
            result.push(cursor.value);
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetAllHistories() {
    const db = await openDb();
    const store = tx(db, 'histories');
    return new Promise((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetHistoriesByDateRange(startDate, endDate) {
    const db = await openDb();
    const store = tx(db, 'histories');
    const index = store.index('by_date');
    const result = [];
    return new Promise((resolve, reject) => {
        const req = index.openCursor(IDBKeyRange.bound(startDate, endDate));
        req.onsuccess = (e) => {
            const cursor = e.target.result;
            if (!cursor) return resolve(result);
            result.push(cursor.value);
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

export async function idbClearHistories() {
    const db = await openDb();
    const store = tx(db, 'histories', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

// Delete history by logical id (UUID 'id') or physical key (auto 'key')
export async function idbDeleteHistory(historyId) {
    const db = await openDb();
    const store = tx(db, 'histories', 'readwrite');
    return new Promise((resolve, reject) => {
        // Try direct delete assuming caller passed physical key
        const directReq = store.delete(historyId);
        directReq.onsuccess = () => {
            // Even if direct delete did nothing (wrong key), also scan by logical id
            const cursorReq = store.openCursor();
            cursorReq.onsuccess = (e) => {
                const cursor = e.target.result;
                if (!cursor) return resolve();
                const value = cursor.value;
                if (value && (value.id === historyId || value.key === historyId)) {
                    const delReq = cursor.delete();
                    delReq.onsuccess = () => {
                        resolve();
                    };
                    delReq.onerror = () => reject(delReq.error);
                } else {
                    cursor.continue();
                }
            };
            cursorReq.onerror = () => {
                // If scanning fails, still resolve from direct delete
                resolve();
            };
        };
        directReq.onerror = () => {
            // If direct delete fails, attempt scan/delete by logical id
            const cursorReq = store.openCursor();
            cursorReq.onsuccess = (e) => {
                const cursor = e.target.result;
                if (!cursor) return resolve();
                const value = cursor.value;
                if (value && (value.id === historyId || value.key === historyId)) {
                    const delReq = cursor.delete();
                    delReq.onsuccess = () => resolve();
                    delReq.onerror = () => reject(delReq.error);
                } else {
                    cursor.continue();
                }
            };
            cursorReq.onerror = () => reject(cursorReq.error);
        };
    });
}

// ===== 앱 설정 관리 =====

export async function idbSetSetting(key, value) {
    const db = await openDb();
    const store = tx(db, 'settings', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.put({ key, value });
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetSetting(key) {
    const db = await openDb();
    const store = tx(db, 'settings');
    return new Promise((resolve, reject) => {
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result?.value);
        req.onerror = () => reject(req.error);
    });
}

export async function idbDeleteSetting(key) {
    const db = await openDb();
    const store = tx(db, 'settings', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.delete(key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetAllSettings() {
    const db = await openDb();
    const store = tx(db, 'settings');
    return new Promise((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => {
            const settings = {};
            req.result.forEach(item => {
                settings[item.key] = item.value;
            });
            resolve(settings);
        };
        req.onerror = () => reject(req.error);
    });
}

export async function idbPutSettings(settingsObj) {
    const db = await openDb();
    const store = tx(db, 'settings', 'readwrite');
    const entries = Object.entries(settingsObj);
    if (entries.length === 0) return Promise.resolve();
    return new Promise((resolve, reject) => {
        let pending = entries.length;
        entries.forEach(([key, value]) => {
            const req = store.put({ key, value });
            req.onsuccess = () => { if (--pending === 0) resolve(); };
            req.onerror = () => reject(req.error);
        });
    });
}

// ===== 마이그레이션 및 초기화 =====

export async function migrateLocalStorageToIdb(localCustomers) {
    let backupData = null;

    try {
        const flat = Object.entries(localCustomers || {}).flatMap(([stage, list]) =>
            (list || []).map(c => ({ ...c, stage }))
        );

        console.log('🔄 고객 데이터 마이그레이션 시작:', { total: flat.length });

        // 1단계: 기존 IndexedDB 데이터 백업
        const existingCustomers = await idbGetAllCustomers();
        console.log('📊 기존 IndexedDB 고객 수:', existingCustomers.length);

        if (existingCustomers.length > 0) {
            backupData = {
                customers: existingCustomers,
                timestamp: new Date().toISOString(),
                type: 'pre_migration_backup'
            };
            console.log('💾 기존 데이터 백업 완료');
        }

        // 2단계: 데이터 검증
        const validCustomers = flat.filter(customer => {
            const isValid = customer.id && customer.name && customer.phone;
            if (!isValid) {
                console.warn('⚠️ 유효하지 않은 고객 데이터 건너뜀:', customer);
            }
            return isValid;
        });

        console.log('✅ 데이터 검증 완료:', {
            original: flat.length,
            valid: validCustomers.length,
            invalid: flat.length - validCustomers.length
        });

        // 3단계: 안전한 병합
        if (existingCustomers.length > 0) {
            const mergedCustomers = [...existingCustomers];
            let updatedCount = 0;
            let addedCount = 0;

            for (const localCustomer of validCustomers) {
                const existingIndex = mergedCustomers.findIndex(c => c.id === localCustomer.id);
                if (existingIndex >= 0) {
                    // 기존 고객 업데이트 (localStorage 우선)
                    mergedCustomers[existingIndex] = localCustomer;
                    updatedCount++;
                    console.log(`🔄 고객 업데이트: ${localCustomer.name} (ID: ${localCustomer.id})`);
                } else {
                    // 새로운 고객 추가
                    mergedCustomers.push(localCustomer);
                    addedCount++;
                    console.log(`➕ 새 고객 추가: ${localCustomer.name} (ID: ${localCustomer.id})`);
                }
            }

            // 4단계: 안전한 저장 (트랜잭션 사용)
            try {
                await idbClearCustomers();
                await idbPutCustomers(mergedCustomers);
            } catch (storageError) {
                console.error('❌ 저장소 용량 부족 또는 저장 실패:', storageError);
                // 저장소 용량 부족 시 백업 데이터 복구
                if (backupData && backupData.customers) {
                    console.log('🔄 저장 실패, 백업 데이터 복구 시도...');
                    await idbClearCustomers();
                    await idbPutCustomers(backupData.customers);
                    console.log('✅ 백업 데이터 복구 완료');
                }
                throw new Error('저장소 용량이 부족하거나 저장에 실패했습니다.');
            }

            console.log('✅ 고객 데이터 병합 완료:', {
                total: mergedCustomers.length,
                updated: updatedCount,
                added: addedCount,
                existing: existingCustomers.length
            });
        } else {
            // IndexedDB가 비어있으면 localStorage 데이터만 저장
            await idbPutCustomers(validCustomers);
            console.log('✅ 고객 데이터 신규 저장 완료:', { total: validCustomers.length });
        }

        // 5단계: 마이그레이션 완료 플래그 설정
        await idbSetSetting('customers_migrated', true);
        await idbSetSetting('customers_migration_date', new Date().toISOString());

        // 6단계: localStorage 정리 (마이그레이션 성공 후)
        try {
            localStorage.removeItem('customerData');
            console.log('🧹 localStorage 고객 데이터 정리 완료');
        } catch (e) {
            console.warn('localStorage 정리 실패:', e);
        }

        return {
            success: true,
            count: validCustomers.length,
            backup: backupData,
            stats: {
                original: flat.length,
                valid: validCustomers.length,
                invalid: flat.length - validCustomers.length
            }
        };
    } catch (e) {
        console.error('❌ 고객 데이터 마이그레이션 실패:', e);

        // 실패 시 백업 데이터 복구 시도
        if (backupData && backupData.customers) {
            try {
                console.log('🔄 마이그레이션 실패, 백업 데이터 복구 시도...');
                await idbClearCustomers();
                await idbPutCustomers(backupData.customers);
                console.log('✅ 백업 데이터 복구 완료');
            } catch (restoreError) {
                console.error('❌ 백업 데이터 복구도 실패:', restoreError);
            }
        }

        return { success: false, error: e, backup: backupData };
    }
}

export async function migrateHistoryStoreToIdb() {
    let backupData = null;

    try {
        // localStorage 데이터 안전하게 파싱
        let store = {};
        try {
            const rawData = localStorage.getItem('customerHistory') || '{}';
            store = JSON.parse(rawData);
        } catch (parseError) {
            console.error('❌ localStorage 히스토리 데이터 파싱 실패:', parseError);
            console.log('손상된 데이터:', localStorage.getItem('customerHistory'));
            // 손상된 데이터는 빈 객체로 처리
            store = {};
        }

        const flat = Object.entries(store).flatMap(([customerId, list]) =>
            (list || []).map(h => ({
                ...h,
                customerId: Number(customerId),
                followUp: h.followUp || null
            }))
        );

        console.log('🔄 히스토리 마이그레이션 시작:', {
            total: flat.length,
            withFollowUp: flat.filter(h => h.followUp).length
        });

        // 1단계: 기존 IndexedDB 히스토리 백업
        const existingHistories = await idbGetAllHistories();
        console.log('📊 기존 IndexedDB 히스토리 수:', existingHistories.length);

        if (existingHistories.length > 0) {
            backupData = {
                histories: existingHistories,
                timestamp: new Date().toISOString(),
                type: 'pre_migration_backup'
            };
            console.log('💾 기존 히스토리 백업 완료');
        }

        // 2단계: 데이터 검증
        const validHistories = flat.filter(history => {
            const isValid = history.id && history.customerId && history.note && history.at;
            if (!isValid) {
                console.warn('⚠️ 유효하지 않은 히스토리 데이터 건너뜀:', history);
            }
            return isValid;
        });

        console.log('✅ 히스토리 데이터 검증 완료:', {
            original: flat.length,
            valid: validHistories.length,
            invalid: flat.length - validHistories.length
        });

        // 3단계: 안전한 병합
        if (existingHistories.length > 0) {
            const mergedHistories = [...existingHistories];
            let updatedCount = 0;
            let addedCount = 0;

            for (const localHistory of validHistories) {
                const existingIndex = mergedHistories.findIndex(h => h.id === localHistory.id);
                if (existingIndex >= 0) {
                    // 기존 히스토리 업데이트 (localStorage 우선)
                    mergedHistories[existingIndex] = localHistory;
                    updatedCount++;
                    console.log(`🔄 히스토리 업데이트: ID ${localHistory.id}`);
                } else {
                    // 새로운 히스토리 추가
                    mergedHistories.push(localHistory);
                    addedCount++;
                    console.log(`➕ 새 히스토리 추가: ID ${localHistory.id}`);
                }
            }

            // 4단계: 안전한 저장
            await idbClearHistories();
            await idbPutHistories(mergedHistories);

            console.log('✅ 히스토리 병합 완료:', {
                total: mergedHistories.length,
                updated: updatedCount,
                added: addedCount,
                existing: existingHistories.length
            });
        } else {
            // IndexedDB가 비어있으면 localStorage 데이터만 저장
            await idbPutHistories(validHistories);
            console.log('✅ 히스토리 신규 저장 완료:', { total: validHistories.length });
        }

        // 5단계: 마이그레이션 완료 플래그 설정
        await idbSetSetting('histories_migrated', true);
        await idbSetSetting('histories_migration_date', new Date().toISOString());

        // 6단계: localStorage 정리 (마이그레이션 성공 후)
        try {
            localStorage.removeItem('customerHistory');
            console.log('🧹 localStorage 히스토리 데이터 정리 완료');
        } catch (e) {
            console.warn('localStorage 정리 실패:', e);
        }

        return {
            success: true,
            count: validHistories.length,
            backup: backupData,
            stats: {
                original: flat.length,
                valid: validHistories.length,
                invalid: flat.length - validHistories.length,
                withFollowUp: validHistories.filter(h => h.followUp).length
            }
        };
    } catch (e) {
        console.error('❌ 히스토리 마이그레이션 실패:', e);

        // 실패 시 백업 데이터 복구 시도
        if (backupData && backupData.histories) {
            try {
                console.log('🔄 히스토리 마이그레이션 실패, 백업 데이터 복구 시도...');
                await idbClearHistories();
                await idbPutHistories(backupData.histories);
                console.log('✅ 히스토리 백업 데이터 복구 완료');
            } catch (restoreError) {
                console.error('❌ 히스토리 백업 데이터 복구도 실패:', restoreError);
            }
        }

        return { success: false, error: e, backup: backupData };
    }
}

// 마이그레이션 진행 중 플래그 (중복 실행 방지)
let migrationInProgress = false;

export async function migrateAllLocalStorageData() {
    // 중복 실행 방지
    if (migrationInProgress) {
        console.log('⚠️ 마이그레이션이 이미 진행 중입니다. 건너뜀.');
        return { success: true, message: '마이그레이션 진행 중' };
    }

    migrationInProgress = true;

    try {
        await openDb();

        // localStorage 데이터 존재 여부 확인
        const hasLocalCustomers = localStorage.getItem('customerData');
        const hasLocalHistories = localStorage.getItem('customerHistory');

        console.log('마이그레이션 필요성 확인:', {
            hasLocalCustomers: !!hasLocalCustomers,
            hasLocalHistories: !!hasLocalHistories
        });

        // 고객 데이터 마이그레이션
        const customersMigrated = await idbGetSetting('customers_migrated');
        if (!customersMigrated && hasLocalCustomers) {
            console.log('고객 데이터 마이그레이션 시작...');

            // localStorage 데이터 안전하게 파싱
            let localCustomers = {};
            try {
                localCustomers = JSON.parse(hasLocalCustomers);
            } catch (parseError) {
                console.error('❌ localStorage 고객 데이터 파싱 실패:', parseError);
                console.log('손상된 데이터:', hasLocalCustomers);
                // 손상된 데이터는 빈 객체로 처리
                localCustomers = {};
            }

            const result = await migrateLocalStorageToIdb(localCustomers);
            if (result.success) {
                console.log('✅ 고객 데이터 마이그레이션 완료');
            } else {
                console.error('❌ 고객 데이터 마이그레이션 실패:', result.error);
            }
        } else if (customersMigrated) {
            console.log('✅ 고객 데이터 이미 마이그레이션됨');
        }

        // 히스토리 마이그레이션
        const historiesMigrated = await idbGetSetting('histories_migrated');
        if (!historiesMigrated && hasLocalHistories) {
            console.log('히스토리 마이그레이션 시작...');
            const result = await migrateHistoryStoreToIdb();
            if (result.success) {
                console.log('✅ 히스토리 마이그레이션 완료');
            } else {
                console.error('❌ 히스토리 마이그레이션 실패:', result.error);
            }
        } else if (historiesMigrated) {
            console.log('✅ 히스토리 이미 마이그레이션됨');
        }

        console.log('🎉 전체 마이그레이션 프로세스 완료');
        return { success: true };
    } catch (e) {
        console.error('❌ 전체 마이그레이션 실패:', e);
        return { success: false, error: e };
    } finally {
        // 마이그레이션 완료 후 플래그 해제
        migrationInProgress = false;
    }
}

export async function clearAllData() {
    try {
        console.log('🗑️ 데이터 초기화 시작... (IndexedDB 전체 삭제)');

        // 1) 현 DB 커넥션 안전 종료
        try {
            if (dbInstance) {
                dbInstance.close();
                dbInstance = null;
                dbPromise = null;
            }
        } catch (_) { }

        // 2) DB 자체 삭제
        await new Promise((resolve, reject) => {
            const req = indexedDB.deleteDatabase(DB_NAME);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
            req.onblocked = () => {
                console.warn('IndexedDB 삭제가 다른 탭에 의해 지연되고 있습니다.');
                resolve();
            };
        });
        console.log('✅ IndexedDB 삭제 완료');

        // 3) 관련 localStorage 키 제거(선택)
        try {
            localStorage.removeItem('customerData');
            localStorage.removeItem('customerHistory');
        } catch (_) { }
        console.log('✅ localStorage 정리 완료');

        return { success: true };
    } catch (e) {
        console.error('❌ 데이터 초기화 실패:', e);
        return { success: false, error: e };
    }
}

// ===== 데이터 백업 및 복구 =====

export async function backupAllData() {
    try {
        await openDb();

        const customers = await idbGetAllCustomers();
        const histories = await idbGetAllHistories();
        const workDiaries = await idbGetWorkDiaries();
        const settings = await idbGetAllSettings();

        const backup = {
            timestamp: new Date().toISOString(),
            version: '3.1',
            customers,
            histories,
            workDiaries: workDiaries || [],
            settings: settings || {},
            metadata: {
                customerCount: customers.length,
                historyCount: histories.length,
                workDiaryCount: workDiaries?.length || 0,
                settingsCount: Object.keys(settings || {}).length,
                backupType: 'full'
            }
        };

        console.log('데이터 백업 완료:', backup.metadata);
        return { success: true, backup };
    } catch (e) {
        console.error('데이터 백업 실패:', e);
        return { success: false, error: e };
    }
}

export async function restoreFromBackup(backup) {
    try {
        await openDb();

        if (!backup || !backup.customers || !backup.histories) {
            throw new Error('유효하지 않은 백업 데이터입니다.');
        }

        console.log('데이터 복구 시작:', {
            customers: backup.customers.length,
            histories: backup.histories.length,
            workDiaries: backup.workDiaries?.length || 0,
            settings: backup.settings ? Object.keys(backup.settings).length : 0,
            timestamp: backup.timestamp
        });

        // 기존 데이터 백업 (복구 실패 시 롤백용)
        const currentBackup = await backupAllData();

        try {
            // 데이터 복구
            await idbClearCustomers();
            await idbClearHistories();
            if (backup.workDiaries && backup.workDiaries.length > 0) {
                await idbClearWorkDiaries();
            }

            await idbPutCustomers(backup.customers);
            await idbPutHistories(backup.histories);
            
            // 업무일지 복구 (버전 3.0 이상의 백업에만 포함)
            if (backup.workDiaries && Array.isArray(backup.workDiaries)) {
                for (const diary of backup.workDiaries) {
                    await idbPutWorkDiary(diary);
                }
            }

            // 설정 복구 (버전 3.1 이상의 백업에만 포함)
            if (backup.settings && typeof backup.settings === 'object') {
                await idbPutSettings(backup.settings);
            }

            console.log('✅ 데이터 복구 완료');
            return { success: true, rollbackData: currentBackup.backup };
        } catch (restoreError) {
            // 복구 실패 시 롤백
            console.error('복구 실패, 롤백 시도:', restoreError);
            if (currentBackup.success) {
                await idbClearCustomers();
                await idbClearHistories();
                await idbClearWorkDiaries();
                await idbPutCustomers(currentBackup.backup.customers);
                await idbPutHistories(currentBackup.backup.histories);
                if (currentBackup.backup.workDiaries && Array.isArray(currentBackup.backup.workDiaries)) {
                    for (const diary of currentBackup.backup.workDiaries) {
                        await idbPutWorkDiary(diary);
                    }
                }
                if (currentBackup.backup.settings && typeof currentBackup.backup.settings === 'object') {
                    await idbPutSettings(currentBackup.backup.settings);
                }
                console.log('✅ 롤백 완료');
            }
            throw restoreError;
        }
    } catch (e) {
        console.error('데이터 복구 실패:', e);
        return { success: false, error: e };
    }
}

// ===== 데이터 무결성 검증 =====

export async function validateDataIntegrity() {
    try {
        await openDb();

        const customers = await idbGetAllCustomers();
        const histories = await idbGetAllHistories();

        const issues = [];

        // 고객 데이터 검증
        customers.forEach((customer, index) => {
            if (!customer.id || !customer.name || !customer.phone) {
                issues.push({
                    type: 'customer',
                    index,
                    id: customer.id,
                    issue: '필수 필드 누락',
                    data: customer
                });
            }

            if (!['prospect', 'preparation', 'appointment', 'consultation', 'closed', 'rejected', 'bank'].includes(customer.stage)) {
                issues.push({
                    type: 'customer',
                    index,
                    id: customer.id,
                    issue: '유효하지 않은 단계',
                    data: customer
                });
            }
        });

        // 히스토리 데이터 검증
        histories.forEach((history, index) => {
            if (!history.id || !history.customerId || !history.note || !history.at) {
                issues.push({
                    type: 'history',
                    index,
                    id: history.id,
                    issue: '필수 필드 누락',
                    data: history
                });
            }

            // 고객 ID가 실제 고객에 존재하는지 확인
            const customerExists = customers.some(c => c.id === history.customerId);
            if (!customerExists) {
                issues.push({
                    type: 'history',
                    index,
                    id: history.id,
                    issue: '존재하지 않는 고객 ID 참조',
                    data: history
                });
            }
        });

        const result = {
            success: issues.length === 0,
            totalCustomers: customers.length,
            totalHistories: histories.length,
            issues,
            summary: {
                validCustomers: customers.length - issues.filter(i => i.type === 'customer').length,
                validHistories: histories.length - issues.filter(i => i.type === 'history').length,
                totalIssues: issues.length
            }
        };

        if (issues.length > 0) {
            console.warn('⚠️ 데이터 무결성 문제 발견:', result);
        } else {
            console.log('✅ 데이터 무결성 검증 통과');
        }

        return result;
    } catch (e) {
        console.error('데이터 무결성 검증 실패:', e);
        return { success: false, error: e };
    }
}

export async function repairDataIntegrity() {
    try {
        const validation = await validateDataIntegrity();

        if (validation.success) {
            console.log('✅ 데이터 수리가 필요하지 않습니다.');
            return { success: true, message: '데이터가 정상입니다.' };
        }

        console.log('🔧 데이터 수리 시작...');

        const customers = await idbGetAllCustomers();
        const histories = await idbGetAllHistories();

        // 손상된 고객 데이터 제거
        const validCustomers = customers.filter(customer =>
            customer.id && customer.name && customer.phone &&
            ['prospect', 'preparation', 'appointment', 'consultation', 'closed', 'rejected', 'bank'].includes(customer.stage)
        );

        // 손상된 히스토리 데이터 제거 (고객 ID가 유효한 것만)
        const validCustomerIds = new Set(validCustomers.map(c => c.id));
        const validHistories = histories.filter(history =>
            history.id && history.customerId && history.note && history.at &&
            validCustomerIds.has(history.customerId)
        );

        // 수리된 데이터 저장
        await idbClearCustomers();
        await idbClearHistories();

        await idbPutCustomers(validCustomers);
        await idbPutHistories(validHistories);

        const repairResult = {
            success: true,
            original: {
                customers: customers.length,
                histories: histories.length
            },
            repaired: {
                customers: validCustomers.length,
                histories: validHistories.length
            },
            removed: {
                customers: customers.length - validCustomers.length,
                histories: histories.length - validHistories.length
            }
        };

        console.log('✅ 데이터 수리 완료:', repairResult);
        return repairResult;

    } catch (e) {
        console.error('데이터 수리 실패:', e);
        return { success: false, error: e };
    }
}

// ===== 업무일지 데이터 관리 =====

export async function idbPutWorkDiary(diary) {
    const db = await openDb();
    const store = tx(db, 'workDiaries', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.put(diary);
        req.onsuccess = () => resolve(diary);
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetWorkDiary(id) {
    const db = await openDb();
    const store = tx(db, 'workDiaries');
    return new Promise((resolve, reject) => {
        const req = store.get(id);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export async function idbGetWorkDiaries() {
    const db = await openDb();
    const store = tx(db, 'workDiaries');
    const index = store.index('by_date');
    const result = [];
    return new Promise((resolve, reject) => {
        const req = index.openCursor(null, 'prev'); // 역순 (최신순)
        req.onsuccess = (e) => {
            const cursor = e.target.result;
            if (!cursor) return resolve(result);
            result.push(cursor.value);
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

export async function idbDeleteWorkDiary(id) {
    const db = await openDb();
    const store = tx(db, 'workDiaries', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.delete(id);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

export async function idbClearWorkDiaries() {
    const db = await openDb();
    const store = tx(db, 'workDiaries', 'readwrite');
    return new Promise((resolve, reject) => {
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

